import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AgentMode } from '~api';

/**
 * @desc The agent interaction mode selected by the user (Charlie rev 2, H2-181).
 * `answer` — H2Answer: fast, direct responses from a single reference source.
 * `analyse` — H2Analyse: detailed, thoughtful responses cross-referencing sources.
 */
export type ChatMode = 'answer' | 'analyse';

type ChatModeState = {
  mode: ChatMode;
  /** Whether the user has been through the mode-selection screen at least once. */
  hasConfirmedSelection: boolean;
  setMode: (mode: ChatMode) => void;
  confirmSelection: (mode: ChatMode) => void;
};

export const useChatModeStore = create<ChatModeState>()(
  persist(
    (set) => ({
      // Answer is the resting default: cheaper and faster; Analyse is an
      // explicit per-chat opt-in (composer dropdown or first-run picker).
      mode: 'answer',
      hasConfirmedSelection: false,

      setMode: (mode) => set({ mode }),

      confirmSelection: (mode) => set({ mode, hasConfirmedSelection: true }),
    }),
    { name: 'h2gc-chat-mode' },
  ),
);

/**
 * @desc Maps the user-facing Charlie mode onto the wire-level agent mode sent
 * with a prompt. `analyse` is the full-analysis persona (the backend default);
 * `answer` maps onto the fast, no-frills persona.
 */
export const toAgentMode = (mode: ChatMode): AgentMode.T =>
  mode === 'answer' ? AgentMode.Modes.NoFuss : AgentMode.Modes.Analyst;
